import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

const inboundTruckApi = {
    addItemsToInboundTruck: async (id, item) => {
        const url = `api/logistics/v1/inbound/trucks/${id}/items`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
    },
    cancelTruck: async (truckID) => {
        const url = `api/logistics/v1/inbound/trucks/${truckID}/cancel`;
        return mmFetchWithResponse(url, {
            method: 'POST',
        });
    },
    createInboundCarrier: async (formData) => {
        const url = `api/logistics/v1/inbound/carriers`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(formData),
        });
    },
    createTruck: async (item) => {
        const url = `api/logistics/v1/inbound/trucks`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
    },
    createTruckTemperature: async (id, stopID, item) => {
        const url = `api/logistics/v1/inbound/trucks/${id}/stop/${stopID}/temperature/create`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
    },
    downloadTruckShipList: async (truckID, facilityID, facilityName) => {
        const url = `api/logistics/v1/inbound/trucks/${truckID}/shipList?facilityID=${facilityID}`;
        const filename = `T-${facilityName}-${truckID}.pdf`;

        const res = await mmFetch(url);

        if (res.ok === true) {
            await fileDownload(res, filename);
        } else {
            const decodedResponse = await res.json();
            throw new Error(decodedResponse.msg);
        }
    },
    downloadTruckTempSummary: async (truckID, stopID) => {
        const url = `api/logistics/v1/inbound/trucks/${truckID}/stop/${stopID}/tempSummary`;
        const filename = `truck_temps_${truckID}.pdf`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
            }
        } catch (e) {
            logError(e);
        }
    },
    editTruckDetails: async (truckID, item) => {
        const url = `api/logistics/v1/inbound/trucks/${truckID}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
    },
    editTruckFacilityStopDetails: async (facilityStopID, item) => {
        const url = `api/logistics/v1/inbound/trucks/stops/${facilityStopID}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
    },
    getPOsNotOnTruck: async (facilityID) => {
        const url = `api/logistics/v1/inbound/trucks/purchaseOrders/unassigned?facilityID=${facilityID}`;
        return mmFetchWithResponse(url);
    },
    getPOItemsOnPartialPOTrucks: async (truckID, poID, skusNotOnTruck) => {
        let url = `api/logistics/v1/inbound/trucks/purchaseOrders/partialPoTruckItems/${truckID}/${poID}`;

        if (skusNotOnTruck) {
            url += `?skusNotOnTruck=${skusNotOnTruck}`;
        }

        return mmFetchWithResponse(url);
    },
    getTruckDetails: async (truckID, facilityID, poType = 'full') => {
        const url = `api/logistics/v1/inbound/trucks/${truckID}?facilityID=${facilityID}&poType=${poType}`;
        return mmFetchWithResponse(url);
    },
    getInboundOptions: async () => {
        const url = `api/logistics/v1/inbound/options`;
        return mmFetchWithResponse(url);
    },
    getTrucks: async (facilityID, searchObj) => {
        let url = `api/logistics/v1/inbound/trucks?facilityID=${facilityID}`;
        for (const key in searchObj) {
            if (Object.prototype.hasOwnProperty.call(searchObj, key)) {
                url += `&${encodeURIComponent(key)}=${encodeURIComponent(searchObj[key])}`;
            }
        }
        return mmFetchWithResponse(url);
    },
    getTruckTemperatures: async (truckId, stopId) => {
        const url = `api/logistics/v1/inbound/trucks/${truckId}/temperatures?truckStopID=${stopId}`;
        return mmFetchWithResponse(url);
    },
    receiveTruck: async ({ truckID }) => {
        const url = `api/logistics/v1/inbound/trucks/receiveTruck/${truckID}`;
        return mmFetchWithResponse(url, {
            method: 'POST',
        });
    },
    removeItemFromInboundTruck: async (id, poID) => {
        const url = `api/logistics/v1/inbound/trucks/${id}/item/${poID}/remove`;

        return mmFetchWithResponse(url, {
            method: 'POST',
        });
    },
    removeItemFromPartialPOTruck: async (truckID, poID, poMapID) => {
        const url = `api/logistics/v1/inbound/trucks/${truckID}/item/${poID}/${poMapID}/remove`;

        return mmFetchWithResponse(url, {
            method: 'POST',
        });
    },
    removePackagingItemFromInboundTruck: async (id, ppoID) => {
        const url = `api/logistics/v1/inbound/trucks/${id}/packaging/${ppoID}/remove`;
        return mmFetchWithResponse(url, {
            method: 'POST',
        });
    },
    uploadTruckTemperatures: async (id, stopID, data) => {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
        });

        const url = `api/logistics/v1/inbound/trucks/${id}/stop/${stopID}/temperatures`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    getTruckStopEvents: async () => {
        const url = `api/logistics/v1/inbound-trucks/truck-stop-events`;
        return mmFetchWithResponse(url);
    },
    exportTruckStopEvents: async () => {
        const url = `api/logistics/v1/inbound-trucks/truck-stop-events/export`;
        const res = await mmFetch(url);

        if (res.ok) {
            await fileDownload(res, 'truck_stop_events.csv');
        } else {
            const decodedResponse = await res.json();
            throw new Error(decodedResponse.msg);
        }
    },
    getPartialTruckPOs: async (facilityID, deliveryType) => {
        const url = `api/logistics/v1/inbound/trucks/purchaseOrders/partialTruckPurchaseOrders/${facilityID}?deliveryType=${deliveryType}`;
        return mmFetchWithResponse(url);
    },
    getPOItemsAvailableForPartialPOTrucks: async (poID) => {
        const url = `api/logistics/v1/inbound/trucks/purchaseOrders/partialPoTruckItems/${poID}`;
        return mmFetchWithResponse(url);
    },
};

export { inboundTruckApi };
